/**
 * @file SiteHeader.tsx
 * @description Top navigation bar with Ultimind logo and primary navigation links.
 */

import { Link } from 'react-router'
import { useState } from 'react'
import { logoBase64 } from '@/assets/logo-base64'

const LOGO_URL = logoBase64

/**
 * SiteHeader component renders the global navigation bar.
 */
export function SiteHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const navItems = [
    { label: 'Home', to: '/' },
    { label: 'About Us', to: '/about' },
    { label: 'Solutions', to: '/solutions' },
    { label: 'Technology', to: '/technology' },
    { label: 'Case Studies', to: '/cases' },
    { label: 'Contact', to: '/contact' },
  ]

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between md:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Ultimind logo"
              className="h-8 w-8 object-contain"
              referrerPolicy="no-referrer"
            />
            <span className="text-lg font-semibold tracking-tight text-slate-50">
              Ultimind
            </span>
          </Link>
          <button
            type="button"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-100 shadow-sm md:hidden"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
            <span>Menu</span>
          </button>
        </div>
        <nav
          id="mobile-nav"
          className={`${isMenuOpen ? 'flex' : 'hidden'} flex-col gap-4 text-sm font-medium text-slate-200 md:flex md:flex-row md:items-center md:gap-6`}
        >
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="transition-colors hover:text-sky-400"
              onClick={() => setIsMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:from-sky-400 hover:to-blue-500 md:hidden"
            onClick={() => setIsMenuOpen(false)}
          >
            Talk to our team
          </Link>
        </nav>
        <div className="hidden md:block">
          <Link
            to="/contact"
            className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:from-sky-400 hover:to-blue-500 md:w-auto"
          >
            Talk to our team
          </Link>
        </div>
      </div>
    </header>
  )
}
